import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { formatSrpSalaryDisplay } from '../../../../../utils/data/format-job-card-labels';
import { convertSalaryToMonthlyEUR } from '../utils/salaryConversion';
import { useJdpSectionExpanded } from '../../../../../hooks/useJdpSectionExpanded';
import { adjustBrandColor } from '../../../../../utils/brand-color/adjust-brand-color';
import { shouldLinkCompanyProfile } from '../../../../../utils/company/should-link-company-profile';
import { companyProfileUrlPath } from '../../../../../utils/routing/company-profile-url-path';
import { resolveSearchHitIsTyomarkkinatori } from '../../../../../utils/search/resolve-search-hit-is-tyomarkkinatori';
import { buildFullCompanyAddress, formatPostalAddressLine } from '../../../../../utils/geocoding/match-job-location-with-company-address';
import { resolveEffectiveBrandColour } from '../../../../../utils/company/resolve-effective-brand-colour';
import { JobDetailPageV2EditorialRail } from './JobDetailPageV2EditorialRail';
import type { OverviewSectionHeaderProps } from '../components/OverviewSectionHeader';
import type { DisplayJob } from '../../../../../types';
import type { FinnishNetSalaryEstimate } from '../utils/estimateFinnishNetSalary';
import type { HubCrossLink } from '../../../../../components/hub/HubCrossLinks';
import { useFormatMessage } from '../../../../../i18n-review/client/Msg';
import { findRoleForNormalisedTitle } from '../../../../../utils/role/find-role-for-title';
import { buildRoleHubPath } from '../../../../../utils/role/role-hub-config';
import { buildLocationSrpPath } from '../../../../../utils/location/build-location-srp-path';
import { findFinlandHubSlugByCityName } from '../../../../../utils/location/finland-hub-config';
import { getFinnishRoleGenitive } from '../../../../../utils/i18n/finnish-role-genitive';
import { getCityInessiveOrNull } from '../../../../../utils/seo/generate-srp-seo-meta';
import { mapEmployerScoreToTier } from '../../../../../utils/employer-score/mapScoreToTier';

export interface JobDetailPageV2Props {
  displayJob: DisplayJob;
  title?: string;
  normalisedTitle?: string;
  companyId?: string | null;
  location?: string;
  remoteness?: string;
  /** TEC-482 follow-up: every structured remote scope (not just the first) shown beside the work
   * model when the new location system is on; empty/undefined renders the plain remoteness as before. */
  remoteScopes?: import('../../../../../utils/location/structured-job-location-display').StructuredRemoteScope[];
  field?: string;
  jobType?: string;
  normalisedRoleLevel?: string;
  requiredLanguages?: string[];
  peopleManagementTier?: string;
  educationLevel?: string;
  minimumYearsOfExperience?: number | null;
  collectiveAgreementId?: string | null;
  collectiveAgreementName?: string | null;
  collectiveAgreementMatchType?: 'explicit' | 'inferred' | 'none' | null;
  collectiveAgreementReasoning?: string | null;
  collectiveAgreementConfidence?: number | null;
  jobLocale: string;
  displayDescription?: string;
  overviewHeaderProps: OverviewSectionHeaderProps;
  applyUrl: string;
  isApplied: boolean;
  setAppliedJobState: (job: any, applied: boolean) => Promise<void>;
  /** When provided, the brand rail's apply button renders a secondary "Tailor my CV" action. */
  onTailorCv?: (opts?: { source?: 'job_page' | 'saved_jobs' | 'deep_link' | 'match_score'; tab?: 'cv' | 'letter' }) => void;
  /** TEC-489, dark-launched: when provided, the brand rail's apply button also offers "Autofill this application". */
  onAutofillApply?: () => void;
  formattedPostedDate: string | null;
  postedDaysAgo: number | null;
  formattedLastSeenDate: string | null;
  formattedApplicationDate: string | null;
  formattedEstimatedExpiryDate: string | null;
  matchScoreData: import('../../../../../utils/data/calculate-job-match-score').MatchScoreBreakdown | null;
  salaryRangeMin?: number;
  salaryRangeMax?: number;
  locationForChart?: string[];
  currency?: string;
  salaryPeriod?: 'annual' | 'monthly' | 'daily' | 'hourly';
  salaryListingProvenance?: import('../../../../../utils/job-detail/resolve-job-salary-listing-provenance').JobSalaryListingProvenance;
  optionalLanguages?: string[];
  perks?: string[];
  jobDetailsRef: React.RefObject<HTMLDivElement>;
  currentLocaleTrends?: {
    roleDistribution: Record<string, number>;
    roleLevelDistribution?: Record<string, number>;
    locationDistribution?: Record<string, number>;
  };
  /** Live Typesense match count for this company's currently-active listings (+data.ts), for CompanyCard's "X open roles" stat — not sourced from the historical companyHiringTrendsByLocale aggregation, which counts every job ever indexed, not just active ones. Null while unavailable (fetch failure, or no companyId). */
  liveOpenRolesCount?: number | null;
  /** The company's 0-100 Employer Score from its Typesense record (fetched in +data.ts); null when unscored. */
  companyEmployerScore?: number | null;
  relatedPages: HubCrossLink[];
  effectiveSimilarJobs: unknown[];
  similarJobsSeeAllHref?: string;
  /** TEC-512: computed in +Page.tsx (estimateFinnishNetSalary). */
  finnishNetSalaryEstimate?: FinnishNetSalaryEstimate | null;
  finnishNetSalaryExplanationText?: string;
  /** TEC-512: deep link from the take-home figure to the tax iceberg calculator, prefilled with this job's gross salary; null when the job isn't Finland-based. */
  taxIcebergHref?: string | null;
  /** TEC-512: the job-alert signup card, built in +Page.tsx (needs jobDetailAlertRole/targetUrl computed there); rendered between the card and SimilarJobsSection. */
  alertCta?: React.ReactNode;
}

/**
 * TEC-512 "editorial rail" redesign of the job detail page's core card (brand rail + content
 * column); breadcrumb/language-switcher/footer/engagement-strip/tailor-cv-modal stay shared,
 * rendered by +Page.tsx around this component. This file computes the derived values shared
 * across the card (brand colour, role hub links, salary labels, Työmarkkinatori handling,
 * section-expand state) once and hands them to JobDetailPageV2EditorialRail, which owns the
 * actual layout. This used to branch between the redesign and a control layout behind an A/B
 * experiment; the experiment gate is gone, this is just how the page looks now, but the
 * compute/render file split was still worth keeping.
 */
export function JobDetailPageV2(props: JobDetailPageV2Props): React.ReactElement {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const {
    displayJob,
    normalisedTitle,
    companyId,
    jobLocale,
    salaryRangeMin,
    salaryRangeMax,
    currency,
    salaryPeriod,
    salaryListingProvenance,
    finnishNetSalaryEstimate = null,
    finnishNetSalaryExplanationText = '',
  } = props;

  const isTyomarkkinatori = resolveSearchHitIsTyomarkkinatori(displayJob as unknown as Record<string, unknown>);
  // Työmarkkinatori listings fall back to purple branding only when the company has no real
  // brandColour of its own (often the case; many TMT postings have no matched company profile
  // at all); same rule the SRP card and V1 detail page apply, via the shared
  // resolveEffectiveBrandColour, so an agency like Osuma or Hunaja Works that DOES have a real
  // colour keeps it instead of being masked by the generic TMT purple on every TMT-sourced job.
  const adjustedBrand = adjustBrandColor(resolveEffectiveBrandColour(displayJob.brandColour, isTyomarkkinatori));
  const companyDisplayName = displayJob.companyName ?? companyId ?? '';
  const canLinkCompanyProfile = shouldLinkCompanyProfile({ companyId: companyId ?? '', isTyomarkkinatori });
  const companyProfileHref = canLinkCompanyProfile && companyId ? companyProfileUrlPath(jobLocale as 'en' | 'fi' | 'sv', companyId) : '';
  // Role hub links: "More {role} jobs" and, when the job's city is a curated hub, the role x city
  // page. Job pages carry most of the site's search impressions, so this is the main path from
  // a listing to its role hub (utils/role/find-role-for-title.ts matches by exact normalisedTitle).
  const roleHubLinks = ((): Array<{ href: string; label: string }> => {
    const loc = jobLocale as 'en' | 'fi' | 'sv';
    const role = findRoleForNormalisedTitle(normalisedTitle, loc);
    if (!role) return [];
    const display = role.displayNames[loc];
    const genitive = loc === 'fi' ? getFinnishRoleGenitive(display) : display;
    const roleLower = loc === 'fi' && genitive !== display ? genitive.toLowerCase() : display;
    const links: Array<{ href: string; label: string }> = [
      {
        href: buildRoleHubPath(role.slug, loc),
        label: formatMessage(
          { id: 'jobDetail.roleHub.moreJobs', defaultMessage: 'More {role} jobs', description: 'Job page link to the role hub; {role} is the role name (Finnish: genitive, lowercase)' },
          { role: roleLower },
        ),
      },
    ];
    const cityRaw = Array.isArray(displayJob.location) ? displayJob.location[0] : undefined;
    const citySlug = findFinlandHubSlugByCityName(cityRaw);
    if (citySlug) {
      const cityName = String(cityRaw).split(',')[0].trim();
      const cityLabel = loc === 'fi' ? (getCityInessiveOrNull(cityName) ?? cityName) : cityName;
      links.push({
        href: `/${loc}${buildLocationSrpPath(citySlug, loc, undefined, role.slug)}`,
        label: formatMessage(
          { id: 'jobDetail.roleHub.jobsInCity', defaultMessage: '{role} jobs in {city}', description: 'Job page link to the role x city page; {role} is the role name (Finnish: genitive), {city} the city (Finnish: inessive)' },
          { role: genitive, city: cityLabel },
        ),
      });
    }
    return links;
  })();
  const companyLinkTitle = formatMessage(
    { id: 'hit.companyLinkTitle', defaultMessage: 'Open company profile: data and insights for {company}', description: 'Tooltip for the employer logo and name link on a job card' },
    { company: companyDisplayName },
  );
  const insightsCueLabel = formatMessage({
    id: 'hit.companyInsights.cue',
    defaultMessage: 'Company insights',
    description: 'Short label under the employer name on a job card; the block links to the company profile',
  });
  const salaryStats = displayJob.companySalaryStats || (displayJob as unknown as { salaryStats?: unknown }).salaryStats;
  const [sectionExpanded, toggleSectionExpanded] = useJdpSectionExpanded();
  // Defaults to the full description (not the tldr) so the SSR HTML that search-engine/Google
  // Jobs indexers read always carries the complete job text, not text hidden behind a click.
  // "Show summary" collapses to the AI tldr; resets per job (not persisted like
  // useJdpSectionExpanded's sections) since it toggles content that's different on every job,
  // not a standing per-user layout preference.
  const [descriptionExpanded, setDescriptionExpanded] = useState(true);
  useEffect(() => {
    setDescriptionExpanded(true);
  }, [displayJob.objectID]);

  // TEC-512 editorial-rail variant only (harmless to compute for control, which never reads
  // them): the collapsible full-original-text toggle defaults open for the same SSR-visibility
  // reason as descriptionExpanded above, and the location map modal's open state.
  const [originalTextExpanded, setOriginalTextExpanded] = useState(true);
  useEffect(() => {
    setOriginalTextExpanded(true);
  }, [displayJob.objectID]);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  // Mobile crossfade mini-header (matches the native app's job screen): fades in once the
  // rail's own identity block has scrolled out of view, fades out again if the user scrolls
  // back up. Desktop doesn't use this at all, the whole rail is sticky instead (BrandRail.tsx).
  const [miniHeaderVisible, setMiniHeaderVisible] = useState(false);
  useEffect(() => {
    const sentinel = document.getElementById('brand-rail-mobile-sentinel');
    if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setMiniHeaderVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [displayJob.objectID]);

  // Real, employer-quoted pay (not extracted-without-quote, not modelled/estimated); shown
  // prominently above the collapsible salary comparison section per its own request, since that
  // section can be collapsed and this is the one number worth surfacing regardless.
  const isRealSalary = salaryListingProvenance === 'employer_quote';
  const hasRawSalary =
    isRealSalary &&
    typeof salaryRangeMin === 'number' &&
    typeof salaryRangeMax === 'number' &&
    salaryRangeMin > 0 &&
    salaryRangeMax > 0;
  const convertedRealSalary = hasRawSalary
    ? convertSalaryToMonthlyEUR(salaryRangeMin!, salaryRangeMax!, currency, salaryPeriod)
    : null;
  const realSalaryLabel = convertedRealSalary
    ? formatSrpSalaryDisplay({
        salaryRangeMin: convertedRealSalary.min,
        salaryRangeMax: convertedRealSalary.max,
        salaryIsEstimate: false,
        locale: intl.locale,
      })
    : null;

  // TEC-512 editorial-rail variant only: the headline-facts salary figure and the standalone
  // salary-comparison figure need a formatted range regardless of provenance (unlike
  // realSalaryLabel above, which is deliberately employer_quote-only for the rail's real-pay
  // pill); same conversion/formatting helpers, just without the provenance gate.
  const hasAnySalary = typeof salaryRangeMin === 'number' && typeof salaryRangeMax === 'number' && salaryRangeMin > 0 && salaryRangeMax > 0;
  const convertedDisplaySalary = hasAnySalary ? convertSalaryToMonthlyEUR(salaryRangeMin!, salaryRangeMax!, currency, salaryPeriod) : null;
  const displaySalaryLabel = convertedDisplaySalary
    ? formatSrpSalaryDisplay({
        salaryRangeMin: convertedDisplaySalary.min,
        salaryRangeMax: convertedDisplaySalary.max,
        salaryIsEstimate: salaryListingProvenance !== 'employer_quote',
        locale: intl.locale,
      })
    : null;

  const companyAddressLine = formatPostalAddressLine(
    buildFullCompanyAddress({
      registryStreet: displayJob.registryStreet,
      registryBuildingNumber: displayJob.registryBuildingNumber,
      registryPostCode: displayJob.registryPostCode,
      registryCity: displayJob.registryCity,
      address_fi: displayJob.address_fi,
      address_sv: displayJob.address_sv,
    }),
  );
  const hasRealCompanyAddress = Boolean(displayJob.companyGeocoordinates && companyAddressLine);

  return (
    <JobDetailPageV2EditorialRail
      {...props}
      matchBreakdownExpanded={sectionExpanded.matchBreakdown}
      onToggleMatchBreakdownExpanded={() => toggleSectionExpanded('matchBreakdown')}
      isTyomarkkinatori={isTyomarkkinatori}
      adjustedBrand={adjustedBrand}
      companyDisplayName={companyDisplayName}
      companyProfileHref={canLinkCompanyProfile ? companyProfileHref : ''}
      roleHubLinks={roleHubLinks}
      companyLinkTitle={companyLinkTitle}
      insightsCueLabel={insightsCueLabel}
      salaryStats={salaryStats}
      realSalaryLabel={realSalaryLabel}
      displaySalaryLabel={displaySalaryLabel}
      displaySalaryMonthly={convertedDisplaySalary ? { min: convertedDisplaySalary.min, max: convertedDisplaySalary.max } : null}
      employerScoreTier={canLinkCompanyProfile && typeof props.companyEmployerScore === 'number' ? mapEmployerScoreToTier(props.companyEmployerScore) : null}
      typicalDaysOpen={displayJob.allSalaryStats?.title?.medianDaysOpen ?? null}
      companyAddressLine={companyAddressLine ?? ''}
      hasRealCompanyAddress={hasRealCompanyAddress}
      finnishNetSalaryEstimate={finnishNetSalaryEstimate}
      finnishNetSalaryExplanationText={finnishNetSalaryExplanationText}
      originalTextExpanded={originalTextExpanded}
      onToggleOriginalTextExpanded={() => setOriginalTextExpanded((v) => !v)}
      mapModalOpen={mapModalOpen}
      onOpenMapModal={() => setMapModalOpen(true)}
      onCloseMapModal={() => setMapModalOpen(false)}
      miniHeaderVisible={miniHeaderVisible}
    />
  );
}
